import { Alert, Button, Col, FormControl, FormGroup, FormLabel, FormSelect, InputGroup, Modal, Row, Tab, Tabs } from "react-bootstrap";
import { IContentComponent, IComponentProperty, IEditorSiteFrame, ISiteMenuTree } from "../../../models/Interfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import BodyTextEditor from "./propertyeditors/BodyTextEditor";
import StringEditor from "./propertyeditors/StringEditor";
import DateEditor from "./propertyeditors/DateEditor";
import CheckedEditor from "./propertyeditors/CheckedEditor";
import ColourPicker from "../../components/common/ColourPicker";
import DecimalNumberEditor from "./propertyeditors/DecimalNumberEditor";
import WholeNumberEditor from "./propertyeditors/WholeNumberEditor";
import ListEditor from "./propertyeditors/ListEditor";
import LinkTargetEditor from "./propertyeditors/LinkTargetEditor";
import ImageSelectEditor from "./propertyeditors/ImageSelectEditor";
import EmbeddedEditor from "./propertyeditors/EmbeddedEditor";
import PageSelectEditor from "./propertyeditors/PageSelectEditor";
import SelectOptionsEditor from "./propertyeditors/SelectOptionsEditor";
import AccordionEditor from "./propertyeditors/AccordionEditor";
import FormFieldsEditor from "./propertyeditors/FormFieldsEditor";
import CarouselEditor from "./propertyeditors/CarouselEditor";
import TabbedContentEditor from "./propertyeditors/TabbedContentEditor";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import HTMLBlockEditor from "./propertyeditors/HTMLBlockEditor";
import { useConfirm } from "../../../config/ConfirmProvider";
import "./componentProperties.scss";
import NewsArticleEditor from "./NewsArticleEditor";
import { SiteMenuService } from "../../../services/SiteMenuService";
import { ContentService } from "../../../services/ContentService";
import { SiteService } from "../../../services/SiteService";
import { frameForProperty } from "../../components/common/editorSiteFrame";

interface IContentComponentEditor {
  ContentComponent: IContentComponent;
  isOpen: boolean;
  onClose: () => void;
  onSaveComponent: (component: IContentComponent) => void | Promise<boolean>;
  focusProperty?: string;
  savedComponent?: IContentComponent;
  presentation?: "panel" | "modal";
  initiallyExpanded?: boolean;
  onDraftChange?: (component: IContentComponent) => void;
  onNestedEditorChange?: (open: boolean) => void;
  onDiscard?: () => void;
  PageLanguage?: string | null;
  editorFrame?: IEditorSiteFrame;
}

const TabNames = {
  Content: "Content",
  Extended: "Extended"
};

const editorComponents = {
  BodyText: BodyTextEditor,
  String: StringEditor,
  Date: DateEditor,
  Checked: CheckedEditor,
  Colour: ColourPicker,
  ColourPicker: ColourPicker,
  DecimalNumber: DecimalNumberEditor,
  WholeNumber: WholeNumberEditor,
  List: ListEditor,
  LinkTarget: LinkTargetEditor,
  Image: ImageSelectEditor,
  Embedded: EmbeddedEditor,
  PageSelector: PageSelectEditor,
  SelectOptions: SelectOptionsEditor,
  AccordionItems: AccordionEditor,
  FormFields: FormFieldsEditor,
  Carousel: CarouselEditor,
  TabItems: TabbedContentEditor,
  HtmlItems: HTMLBlockEditor
};

// Define the valid property type names
type PropertyTypeName = keyof typeof editorComponents;

const resolveEditorComponent = (typeName: string) => {
  return editorComponents[typeName as PropertyTypeName]
    ?? Object.entries(editorComponents).find(([name]) => name.toLowerCase() === typeName.toLowerCase())?.[1];
};

const ContentComponentEditor = (props: IContentComponentEditor) => {
  const [activeTab, setActiveTab] = useState<string>(TabNames.Content);
  const [component, setComponent] = useState<IContentComponent>(props.ContentComponent);
  const componentRef = useRef(component);
  const baselineRef = useRef(JSON.stringify(props.savedComponent ?? props.ContentComponent));
  const [history, setHistory] = useState<IContentComponent[]>([]);
  const [future, setFuture] = useState<IContentComponent[]>([]);
  const [editorRevision, setEditorRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  savingRef.current = saving;
  const [saveError, setSaveError] = useState(false);
  const [expanded, setExpanded] = useState(props.initiallyExpanded ?? false);
  const { requestConfirmation } = useConfirm();
  const dirty = JSON.stringify(component) !== baselineRef.current;
  const panel = props.presentation === "panel" && !expanded;
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [siteMenus, setSiteMenus] = useState<Array<ISiteMenuTree>>([]);
  const menuSvcRef = useRef<SiteMenuService | null>(null);
  const contentSvcRef = useRef<ContentService | null>(null);
  const siteSvcRef = useRef<SiteService | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const updateComponent = (next: IContentComponent) => {
    if (savingRef.current) return;
    if (JSON.stringify(next) === JSON.stringify(componentRef.current)) return;
    const previousComponent = componentRef.current;
    setHistory(previous => [...previous.slice(-49), previousComponent]);
    setFuture([]);
    componentRef.current = next;
    setComponent(next);
    props.onDraftChange?.(next);
    setSaveError(false);
  };

  const restoreComponent = (next: IContentComponent) => {
    componentRef.current = next;
    setComponent(next);
    props.onDraftChange?.(next);
    setEditorRevision(value => value + 1);
  };

  const undo = () => {
    const previous = history[history.length - 1];
    if (!previous || editorOpen || saving) return;
    const current = componentRef.current;
    setFuture(values => [current, ...values]);
    setHistory(values => values.slice(0, -1));
    restoreComponent(previous);
  };

  const redo = () => {
    const next = future[0];
    if (!next || editorOpen || saving) return;
    const current = componentRef.current;
    setHistory(values => [...values, current]);
    setFuture(values => values.slice(1));
    restoreComponent(next);
  };

  const updateColSpan = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = Number(event.target.value);
    if (value) updateComponent({ ...componentRef.current, ColSpan: value });
  };

  const onPublishedValueChange = (propertyId: number, newValue: string): void => {
    const current = componentRef.current;
    updateComponent({ ...current, BaseComponent: { ...current.BaseComponent,
      Properties: current.BaseComponent.Properties.map(property => property.Id === propertyId
        ? { ...property, PublishedValue: newValue } : property) } });
  };

  const onChildEditorOpen = (open: boolean): void => { setEditorOpen(open); };

  const closeEditor = async () => {
    if (saving || editorOpen) return;
    if (!props.onDraftChange && dirty && !await requestConfirmation("Discard your unsaved component changes?", "Discard changes")) return;
    props.onClose();
  };

  const discard = async () => {
    if (saving || editorOpen) return;
    if (dirty && !await requestConfirmation("Discard your unsaved component changes?", "Discard changes")) return;
    props.onDiscard?.();
    props.onClose();
  };

  const onSave = async () => {
    if (saving || editorOpen) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      const result = await props.onSaveComponent(componentRef.current);
      if (result === false) setSaveError(true);
      else baselineRef.current = JSON.stringify(componentRef.current);
    } catch { setSaveError(true); }
    finally { savingRef.current = false; setSaving(false); }
  };

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (!bodyRef.current?.parentElement?.contains(event.target as Node) || editorOpen) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault(); void onSave();
      }
      if (event.key === "Escape" && !document.querySelector('.modal.show:not(.component-editor-modal)')) {
        event.preventDefault(); void closeEditor();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [saving, editorOpen, dirty, props.onClose]);

  const onCopyApiKey = () : void => {
    navigator.clipboard.writeText(props.ContentComponent.ApiTag);
    actionEmitter.emit(EmitEvent.Result, true, `${props.ContentComponent.ApiTag} copied to clipboard`);
  };

  const hasSiteMenuProperty = useMemo(() => {
    return component.BaseComponent.Properties.some(
      (prop) => prop.PropertyName?.toLowerCase() === "sitemenuid"
    );
  }, [component.BaseComponent.Properties]);

  const siteMenuOptions = useMemo(() => {
    return siteMenus.filter(menu => menu.type === "menugroup");
  }, [siteMenus]);

  // First, declare renderProperties function
  const renderProperties = (isExtended: boolean) => {
    return (
      <>
        <Row>
          {component.BaseComponent.Properties
            .filter((prop) => (isExtended ? prop.ShowOnExtendedTab : !prop.ShowOnExtendedTab))
            .map(renderPropertyEditor)}
        </Row>

          {isExtended && (
            <>
              <Row>
                <Col xs={12}>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md={12} xl={6} className="mt-3">
                  <InputGroup>
                    <InputGroupText>Col Span</InputGroupText>
                    <FormSelect 
                      value={component.ColSpan}
                      title="set column span (if section uses columns}"
                      onChange={updateColSpan}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((i) => (
                      <option key={i} value={i}>{`${i}`} {i === 1 ? 'Column' : 'Columns'}</option>
                    ))}
                    </FormSelect>
                  </InputGroup>
                </Col>
                <Col md={12} xl={6} className="mt-3">
                  <InputGroup>
                    <InputGroupText>Api Key</InputGroupText>
                    <FormControl 
                      type="text"
                      value={props.ContentComponent.ApiTag}
                      readOnly={true}>
                    </FormControl>
                    <InputGroupText title="copy api key" style={{cursor: "pointer"}} onClick={onCopyApiKey}>
                      <FontAwesomeIcon icon={faCopy} />
                    </InputGroupText>
                  </InputGroup>
                </Col>
              </Row>
            </>
          )}
      </>
    );
  };

  const getCols = (propertytype: string) : number => {
    let cols: number = 6; // default

    if (propertytype.toLowerCase() === "bodytext" ||
        propertytype.toLowerCase() === "accordionitems" ||
        propertytype.toLowerCase() === "image" ||
        propertytype.toLowerCase() === "formfields" ||
        propertytype.toLowerCase() === "carousel" || 
        propertytype.toLowerCase() === "tabitems" ||
        propertytype.toLowerCase() === "embedded" || 
        propertytype.toLowerCase() === "htmlitems"
      ) {
          cols = 12;
        }

    return cols;
  }

  const hideOnChildEditor = (propertytype: string): boolean => {
    let hide: boolean = true; // default

    if (propertytype.toLowerCase() === "accordionitems" ||
        propertytype.toLowerCase() === "formfields" ||
        propertytype.toLowerCase() === "carousel" ||
        propertytype.toLowerCase() === "tabitems" ||
        propertytype.toLowerCase() === "htmlitems"
    ){
      hide = false;
    }
    return hide;
  }

  // Now, define renderPropertyEditor function
  const renderPropertyEditor = (prop: IComponentProperty) => {
    if (!prop.UserEditable) return null;

    if (hasSiteMenuProperty && prop.PropertyName.toLowerCase() === "sitemenuid") {
      return (
        <Col md={12} xl={6} key={prop.Id} className="mt-3">
          <FormGroup>
            <FormLabel>{prop.DisplayLabel || "Site Menu"}</FormLabel>
            <FormSelect
              value={prop.PublishedValue ?? ""}
              onChange={(e) => onPublishedValueChange(prop.Id, e.target.value)}
              disabled={siteMenuOptions.length === 0}>
              <option value="">Select a site menu...</option>
              {siteMenuOptions.map(menu => (
                <option key={`site-menu-${menu.id}`} value={menu.id}>
                  {menu.language ? `${menu.text} (${menu.language})` : menu.text}
                </option>
              ))}
            </FormSelect>
            {siteMenuOptions.length === 0 && (
              <div className="form-text">No site menus are available for this page language yet.</div>
            )}
          </FormGroup>
        </Col>
      );
    }

    // Cast prop.Type.Name to a valid editor component type
    const EditorComponent = resolveEditorComponent(prop.Type.Name);
    if (!EditorComponent) return <div key={prop.Id}></div>;

    const shouldHideWhileChildEditorOpen = hideOnChildEditor(prop.Type.Name) && prop.UseFrameworkHelper !== true;

    return (
        <Col hidden={editorOpen && shouldHideWhileChildEditorOpen} data-property-name={prop.PropertyName} md={12} xl={getCols(prop.Type.Name)} key={prop.Id} className="mt-3">
          <FormGroup>
            <EditorComponent
              property={prop}
              onValueChange={onPublishedValueChange}
              onEditorOpen={onChildEditorOpen}
              editorHeight={props.presentation === "panel" && !expanded ? 420 : undefined}
              siteFrame={frameForProperty(props.editorFrame, component, prop)}
            />
          </FormGroup>
        </Col>
    );
  };

  // Use memoization after the renderProperties is defined
  const memoizedProperties = useMemo(() => renderProperties(false), [
    component,
    activeTab,
    editorOpen,
    siteMenus,
    expanded,
    props.presentation,
  ]);

  const isNewsArticleComponent = useMemo(() => {
    return component.BaseComponent.Name.toLowerCase() === "newsarticle";
  }, [component.BaseComponent.Name]);

  useEffect(() => {
    if (!menuSvcRef.current) {
      menuSvcRef.current = new SiteMenuService();
    }

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!siteSvcRef.current) {
      siteSvcRef.current = new SiteService();
    }
  }, []);

  useEffect(() => {
    const getMenus = async (): Promise<void> => {
      if (!menuSvcRef.current || !props.isOpen || !hasSiteMenuProperty) {
        if (!hasSiteMenuProperty) {
          setSiteMenus([]);
        }
        return;
      }

      const siteDetails = await siteSvcRef.current?.getSiteDetails();
      const fallbackLanguage = props.PageLanguage || siteDetails?.DefaultLanguage || null;

      const languages = fallbackLanguage
        ? [fallbackLanguage]
        : (await contentSvcRef.current?.getSiteLanguages())?.map(lang => lang.ShortCode).filter(Boolean) ?? [];

      if (languages.length === 0) {
        setSiteMenus([]);
        return;
      }

      const menuLists = await Promise.all(
        languages.map(lang => menuSvcRef.current?.getSiteMenusForLanguage(lang))
      );

      const combinedMenus = menuLists
        .flatMap(menus => menus ?? [])
        .filter((menu, index, array) =>
          array.findIndex(existing => existing.id === menu.id && existing.type === menu.type) === index
        );

      setSiteMenus(combinedMenus);
    };

    getMenus();
  }, [hasSiteMenuProperty, props.PageLanguage, props.isOpen]);

  useEffect(() => {
    const bodyElement = bodyRef.current;

    if (!bodyElement) {
      return;
    }

    const syncNestedEditorState = (): void => {
      const nestedEditorOpen = bodyElement.querySelector('[data-nested-editor-open="true"]') !== null;
      setEditorOpen(nestedEditorOpen);
    };

    syncNestedEditorState();

    const observer = new MutationObserver(syncNestedEditorState);
    observer.observe(bodyElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-nested-editor-open"]
    });

    return () => {
      observer.disconnect();
    };
  }, [activeTab, component, props.isOpen, expanded, editorRevision]);

  useEffect(() => {
    if (!props.focusProperty) return;
    const property = component.BaseComponent.Properties.find(property => property.PropertyName === props.focusProperty);
    if (property) setActiveTab(property.ShowOnExtendedTab ? TabNames.Extended : TabNames.Content);
    const frame = requestAnimationFrame(() => {
      const target = Array.from(bodyRef.current?.querySelectorAll<HTMLElement>('[data-property-name]') ?? [])
        .find(element => element.dataset.propertyName === props.focusProperty);
      target?.scrollIntoView({ block: "nearest" });
      target?.querySelector<HTMLElement>('input, select, textarea, button')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [props.focusProperty, expanded, activeTab]);

  useEffect(() => {
    props.onNestedEditorChange?.(editorOpen || saving);
    return () => props.onNestedEditorChange?.(false);
  }, [editorOpen, saving, props.onNestedEditorChange]);

  const editorBody = (
    <div ref={bodyRef} key={editorRevision} className={`component-properties-body${saving ? " is-saving" : ""}`} aria-busy={saving}>
      {isNewsArticleComponent ? (
        <NewsArticleEditor component={component} editorFrame={props.editorFrame}
          onValueChange={onPublishedValueChange} onColSpanChange={updateColSpan} onEditorOpen={onChildEditorOpen} />
      ) : (
        <Tabs id={`component-properties-${component.Id}`} activeKey={activeTab}
          onSelect={key => setActiveTab(key ?? TabNames.Content)} className="m-0 p-0 mt-2">
          <Tab eventKey={TabNames.Content} title="Content" className="ms-2 me-2">{memoizedProperties}</Tab>
          <Tab eventKey={TabNames.Extended} title="Extended" className="ms-2 me-2">{renderProperties(true)}</Tab>
        </Tabs>
      )}
    </div>
  );
  const actions = !editorOpen && <div className="component-properties-actions">
    <span className="small text-muted">{dirty ? (panel ? "Previewing unsaved changes" : "Unsaved changes") : "No new changes"}</span>
    <Button size="sm" variant="outline-secondary" onClick={undo} disabled={saving || history.length === 0}>Undo</Button>
    <Button size="sm" variant="outline-secondary" onClick={redo} disabled={saving || future.length === 0}>Redo</Button>
    <Button size="sm" variant="success" onClick={() => void onSave()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
    <Button size="sm" variant="outline-secondary" onClick={() => void discard()} disabled={saving}>Discard</Button>
    {saveError && <Alert variant="danger" className="mb-0 w-100">Could not save. Your changes are still here; try again.</Alert>}
  </div>;

  if (panel) return <aside className="component-properties-panel" aria-label={`${component.DisplayName} properties`}>
    <div className="component-properties-header">
      <strong>{component.DisplayName}</strong>
      <Button size="sm" variant="outline-secondary" onClick={() => setExpanded(true)} disabled={editorOpen || saving}>Expand</Button>
      <Button size="sm" variant="outline-secondary" onClick={() => void closeEditor()} disabled={editorOpen || saving}>Close</Button>
    </div>
    {props.onDraftChange && <div className="small text-muted px-3 pt-2">Changes stay in this page until you save or discard them.</div>}
    {editorBody}
    {actions}
  </aside>;

  return <Modal show={props.isOpen} onHide={() => void closeEditor()} size="xl" backdrop="static" enforceFocus={false}
    className={`component-editor-modal${editorOpen ? " component-editor-modal--child-open" : ""}`}>
    <Modal.Header closeButton={!editorOpen && !saving} closeLabel="Close">
      <h4><b>{component.DisplayName}</b>&nbsp;Content</h4>
      {props.presentation === "panel" && <Button size="sm" variant="outline-secondary" className="ms-3"
        disabled={editorOpen || saving} onClick={() => setExpanded(false)}>Dock to panel</Button>}
    </Modal.Header>
    <Modal.Body>{editorBody}</Modal.Body>
    {!editorOpen && <Modal.Footer>{actions}</Modal.Footer>}
  </Modal>;
};

export default ContentComponentEditor;
